import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { HelpLink } from './HelpLink';
import { AddressTaxCheck } from './tax/AddressTaxCheck';
import { TaxStatesPanel } from './tax/TaxStatesPanel';

const API = process.env.REACT_APP_BACKEND_URL;
const day = (v) => (v ? new Date(v).toLocaleDateString() : 'Not recorded');

const RateRow = ({ row, canEdit, token, onSaved }) => {
  const [rate, setRate] = useState(String(row.rate));
  const [date, setDate] = useState(row.effective_date || '');
  const changed = rate !== String(row.rate) || date !== (row.effective_date || '');
  const save = async () => {
    try {
      await axios.put(`${API}/api/admin/tax/jurisdictions/${row.code}`, { rate: parseFloat(rate), effective_date: date, source: 'Updated by admin from NYS Publication 718' },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`${row.name} updated`); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save'); }
  };
  return (
    <tr className="border-b" data-testid={`tax-rate-row-${row.code}`}>
      <td className="py-2 pr-3 text-sm">{row.name}</td>
      <td className="py-2 pr-3 text-xs text-neutral-500">{row.code}</td>
      <td className="py-2 pr-3">{canEdit ? <Input value={rate} onChange={(e) => setRate(e.target.value)} className="w-24 h-8" data-testid={`tax-rate-input-${row.code}`} /> : `${row.rate}%`}</td>
      <td className="py-2 pr-3">{canEdit ? <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-40 h-8" /> : row.effective_date}</td>
      <td className="py-2 pr-3 text-xs text-neutral-500 max-w-[14rem]">{row.source}</td>
      <td className="py-2 pr-3 text-xs text-neutral-500">{day(row.last_verified)}</td>
      <td className="py-2">{canEdit && changed && <Button size="sm" onClick={save} data-testid={`tax-rate-save-${row.code}`}>Save</Button>}</td>
    </tr>
  );
};

const CalculationOrder = ({ steps }) => (
  <details className="bg-white border rounded-lg p-4" data-testid="tax-calculation-order">
    <summary className="text-sm font-medium cursor-pointer">How tax is calculated (in this order)</summary>
    <ol className="mt-2 text-xs text-neutral-600 list-decimal pl-5 space-y-0.5">{steps.map((s) => <li key={s}>{s}</li>)}</ol>
    <p className="mt-2 text-xs text-neutral-500">The billing address alone never decides tax. Accepted or paid records keep their historical tax.</p>
  </details>
);

// Sales Tax Management: everyone with quote/order access can view; only Super Admin / Admin can change
export const TaxRatesTab = ({ token }) => {
  const [cfg, setCfg] = useState(null);
  const [q, setQ] = useState('');
  const load = () => axios.get(`${API}/api/admin/tax/config`, { headers: { Authorization: `Bearer ${token}` } }).then((r) => setCfg(r.data)).catch(() => toast.error('Could not load tax settings'));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  if (!cfg) return <p className="text-sm text-neutral-500 p-6">Loading tax settings…</p>;
  const canEdit = cfg.can_edit;
  const rows = cfg.jurisdictions.filter((r) => `${r.name} ${r.county} ${r.code}`.toLowerCase().includes(q.toLowerCase()));
  const verifyAll = async () => {
    try { await axios.post(`${API}/api/admin/tax/jurisdictions/mark-verified`, {}, { headers: { Authorization: `Bearer ${token}` } }); toast.success('Table marked as verified today'); load(); } catch (e) { toast.error(e.response?.data?.detail || 'Could not save'); }
  };
  return (
    <div className="space-y-6" data-testid="tax-rates-tab">
      <div>
        <h2 className="text-lg font-semibold flex items-center gap-2">Sales Tax Management <HelpLink topic="tax-rates" testId="help-link-tax-rates-page" /></h2>
        <p className="text-sm text-neutral-500">Tax follows the delivery/service address. New York uses the jurisdiction table below; every other state stays off until an Admin deliberately enables it.
          {!canEdit && <span className="ml-1 text-amber-700" data-testid="tax-view-only">View only — Super Admins and Admins make changes.</span>}</p>
      </div>
      <CalculationOrder steps={cfg.calculation_order} />
      {canEdit && <AddressTaxCheck token={token} />}
      <TaxStatesPanel states={cfg.states} canEdit={canEdit} token={token} onSaved={load} />
      <div className="bg-white border rounded-lg p-4">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <p className="text-sm font-medium flex items-center gap-2">New York jurisdictions ({cfg.jurisdictions.length}) <HelpLink topic="tax-new-york" /></p>
          <div className="flex gap-2">
            <Input placeholder="Search county, city or code" value={q} onChange={(e) => setQ(e.target.value)} className="w-56 h-8" data-testid="tax-rates-search" />
            {canEdit && <Button size="sm" variant="outline" onClick={verifyAll} data-testid="tax-rates-mark-verified">Mark table verified today</Button>}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead><tr className="text-left text-xs text-neutral-500 border-b"><th className="py-2">Jurisdiction</th><th>Code</th><th>Combined rate %</th><th>Effective</th><th>Source / publication</th><th>Last verified</th><th /></tr></thead>
            <tbody>{rows.map((r) => <RateRow key={r.code} row={r} canEdit={canEdit} token={token} onSaved={load} />)}</tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
